import { useEffect, useRef, useState } from "react"
import { CareerBlockType, CareerType } from "../../types/mainModulTypes"
import { API } from "../../constants/constants"
import BaseSubheadline from "./BaseSubheadline"
import IconButton from "./IconButton"
import BaseRichtext from "./BaseRichtext"
import PrimaryButton from "./PrimaryButton"
import { useNavigate } from "react-router-dom"

export default function Career(props: CareerType) {
    const [selectedCareerBlock, setselectedCareerBlock] = useState<CareerBlockType | undefined>()
    const navigate = useNavigate()

    const openedBlockRef = useRef<HTMLDivElement>(null)
    const allBlocksRef = useRef<HTMLDivElement>(null)

    const prevSelectedCareerBlock = useRef<CareerBlockType | undefined>(undefined)

    const scrollToElementWithOffset = (element: HTMLDivElement | null) => {
        if (!element) return

        const fixedHeaderOffset = 140
        const elementTop = element.getBoundingClientRect().top + window.scrollY

        window.scrollTo({
            top: Math.max(elementTop - fixedHeaderOffset, 0),
            behavior: "smooth"
        })
    }

    useEffect(() => {
        const previousBlock = prevSelectedCareerBlock.current

        if (selectedCareerBlock && previousBlock !== selectedCareerBlock) {
            scrollToElementWithOffset(openedBlockRef.current)
        }

        if (!selectedCareerBlock && previousBlock) {
            scrollToElementWithOffset(allBlocksRef.current)
        }

        prevSelectedCareerBlock.current = selectedCareerBlock
    }, [selectedCareerBlock])

    return (
        <div className={`w-full py-8 text-neutral`}>
            <div className="bg-base-100 rounded-default py-24">
                <div className="max-w-content mx-auto flex items-center gap-8 px-4 md:flex-row flex-col">
                    <img
                        src={API + props?.careerImage?.url}
                        alt={props?.careerImage?.description}
                        className="flex-grow aspect-[8/7] w-full sm:w-3/5 md:w-2/5 object-cover rounded-default"
                    />
                    <div className="flex-grow ">
                        <BaseSubheadline label={props.careerHeading} />
                        <BaseRichtext content={props.careerText} />
                    </div>
                </div>
            </div>
            {/* Opened CareerBlock */}
            {selectedCareerBlock && (
                <>
                    <div className="max-w-content mx-auto" ref={openedBlockRef}>
                        <div
                            className="rounded-default flex px-12 md:px-16 py-8 max-w-xl text-accent font-semibold mx-4 lg:mx-0 -mt-12"
                            style={{ backgroundColor: selectedCareerBlock.careerJobColor }}>
                            {selectedCareerBlock.careerJobTitle}
                        </div>
                        <div
                            className="w-0 h-0 border-l-[40px] border-l-transparent border-r-[40px] border-r-transparent border-t-[60px] ml-8 md:mb-16"
                            style={{ borderTopColor: selectedCareerBlock.careerJobColor }}
                        />
                    </div>

                    {selectedCareerBlock.carrerJobSection.map((job, index) => (
                        <div key={index} className={`${index % 2 === 0 ? "bg-base-300" : "bg-accent"} px-4  lg:rounded-default py-8`}>
                            <div className="max-w-content mx-auto  flex sm:flex-row flex-col sm:items-end gap-2">
                                <div className={" flex flex-col py-8 justify-start w-full"}>
                                    <BaseSubheadline label={job.carrerJobDescriptionHeading} />
                                    <BaseRichtext content={job.carrerJobDescriptionText} />
                                    {job.carrerJobDescriptionToggle && (
                                        <>
                                            <BaseSubheadline label={job.carrerJobDescriptionHeading2} classProps="pt-4" />
                                            <BaseRichtext content={job.carrerJobDescriptionText2} />
                                        </>
                                    )}
                                    {(job.carrerJobDescriptionButtonApplyToggle || job.carrerJobDescriptionButtonDownloadToggle) && (
                                        <div className="flex flex-col sm:flex-row gap-4  sm:gap-8 pt-4">
                                            {job.carrerJobDescriptionButtonApplyToggle && (
                                                <PrimaryButton
                                                    text={job.carrerJobDescriptionButtonApplyText}
                                                    active={true}
                                                    onClick={() => {
                                                        if (job.carrerJobDescriptionButtonApplyUrl.includes("https://")) {
                                                            window.open(job.carrerJobDescriptionButtonApplyUrl, "_blank")
                                                        } else {
                                                            navigate(job.carrerJobDescriptionButtonApplyUrl)
                                                        }
                                                    }}
                                                />
                                            )}

                                            {job.carrerJobDescriptionButtonDownloadToggle && (
                                                <PrimaryButton
                                                    text={job.carrerJobDescriptionButtonDownloadText}
                                                    active={true}
                                                    onClick={() => {
                                                        if (job.carrerJobDescriptionButtonDownloadUrl.includes("https://")) {
                                                            window.open(job.carrerJobDescriptionButtonDownloadUrl, "_blank")
                                                        } else {
                                                            navigate(job.carrerJobDescriptionButtonDownloadUrl)
                                                        }
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                                {job.carrerJobDescriptionIconToggle && (
                                    <img
                                        src={API + job.carrerJobDescriptionIcon?.url}
                                        alt={job.carrerJobDescriptionIcon?.description || job.carrerJobDescriptionIcon?.name}
                                        className="w-16 object-contain"
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </>
            )}
            {/* All CareerBlocks */}
            <div ref={allBlocksRef} className={`bg-base-100 rounded-default py-8 ${selectedCareerBlock ? "" : "-mt-10"}`}>
                <div className="max-w-content mx-auto grid grid-cols-1 sm:grid-cols-2 auto-rows-fr gap-8">
                    {props.careerBlock?.map((item, index) => (
                        <CareerBlock
                            key={index}
                            selected={item === selectedCareerBlock}
                            label={item.careerJobTitle}
                            color={item.careerJobColor}
                            function={() => setselectedCareerBlock(item)}
                            closeFunction={() => setselectedCareerBlock(undefined)}
                            image={item.careerJobImage?.url}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}

type CareerBlockComponentType = {
    label: string
    color: string
    selected: boolean
    image: string
    function: () => void
    closeFunction: () => void
}

function CareerBlock(props: CareerBlockComponentType) {
    return (
        <div
            style={{ backgroundColor: props.color }}
            className={`px-12 py-12 z-10 flex w-full items-center cursor-pointer justify-center rounded-default relative overflow-hidden transition-transform duration-200 ${
                props.selected ? "bg-cover bg-center" : "hover:scale-105"
            }`}
            onClick={!props.selected ? props.function : () => {}}>
            
            {/* Background image layer - always present but with different opacity */}
            <div
                style={{ 
                    backgroundImage: `url(${API + props.image})`,
                    opacity: props.selected ? 1 : 0.3
                }}
                className="absolute inset-0 bg-cover bg-center transition-opacity duration-300"
            />
            
            {/* Content layer */}
            <div className="relative z-10 flex items-center justify-center w-full">
                {!props.selected && (
                    <div className="text-center px-4 py-2 rounded-lg bg-black bg-opacity-20 backdrop-blur-sm">
                        <p className="text-base-300 font-semibold text-lg drop-shadow-lg">{props.label}</p>
                    </div>
                )}
                {props.selected && (
                    <IconButton
                        function={() => props.closeFunction()}
                        customColor="bg-base-300 text-neutral"
                        closeButton
                        classProps="absolute top-4 right-4 z-50"
                    />
                )}
            </div>
        </div>
    )
}
